import { useMutation } from '@tanstack/react-query';
import {
  Table as AntdTable,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Row,
  Space,
  Typography,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ICalculateItemSeparationPrice,
  ICancelPurchaseInvoiceItemSeparation,
  IMovePurchaseInvoiceItemParams,
  IPurchaseInvoiceSeparation,
} from 'apis/GraphQL/Variables/purchaseInvoice';
import { PurChaseInvoiceApi } from 'apis/PurchaseInvoiceApis';
import { Button, InputForm, Table } from 'components';
import { Collapse, Panel } from 'components/Collapse/Collapse';
import { OptionsMenu } from 'components/OptionsMenu/OptionsMenu';
import { useTitle } from 'contexts/TitleProvider';
import useQueryParam from 'hooks/useQueryPrams';
import {
  IPurchaseSeparationData,
  TpurchaseDetail,
  TPurchaseDetail,
  TPurchaseSeparationDetail,
} from 'interfaces/PurchaseInvoice';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import path from 'routes/Path';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import formatNumber, {
  formatDate,
  toastError,
  toastSuccess,
  totalData,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import SplitWeightModal, {
  ISplitWeightModalData,
} from '../Modals/SplitWeightModal';
import { IPriceDetail } from '../PurchaseInvoiceDetailPage';
import { formatValue } from '../TablePurchaseInvoiceDetail';

const { Summary } = AntdTable;
const { Title, Text } = Typography;
const { RightOutlined, SaveTwoTone } = icons;

interface CustomCollapseProps {
  header: string;
  panelKey: string;
  content: React.ReactNode;
}

interface PurchaseContentProps {
  count: string | number;
  price: string | number;
}

interface ITableItem {
  title?: ReactNode;
  dataSource: any[] | null;
  columns: ColumnsTypeCustom;
}

export const totalKeys: (keyof TpurchaseDetail)[] = [
  'totalGoldWeightThread',
  'q10',
  'measureWeightThread',
  'lossWeightThread',
  'includeWeightThread',
  'totalGoldWeightOfStool',
  'finalPrice',
  // 'measureWeightThreadAccounting',
  // 'lossWeightThreadAccounting',
  // 'includeWeightThreadAccounting',
  // 'q10Accounting',
  // 'totalWeightStoolAccounting',
  // 'finalPriceAccounting',
];

// export const dataIndexes: (keyof TpurchaseDetail)[] = [
//   'totalGoldWeightThread',
//   'q10',
//   'measureWeightThread',
//   'lossWeightThread',
//   'includeWeightThread',
//   'totalGoldWeightOfStool',
//   'unitPrice',
//   'finalPrice',
//   'policyPrice',
// ];

const CustomCollapse = ({ items }: { items: CustomCollapseProps[] }) => {
  const defaultActiveKey = items.map((item) => item.panelKey);

  return (
    <Space direction="vertical" size="middle" className="w-full">
      {items.map((item, index) => (
        <Card
          key={index}
          className="rounded-3xl"
          styles={{ body: { padding: 0 } }}
        >
          <Collapse
            ghost
            className="w-full p-0"
            expandIconPosition="end"
            collapseIcon={<RightOutlined />}
            defaultActiveKey={defaultActiveKey}
          >
            <Panel
              key={item.panelKey}
              header={<Title level={5}>{item.header}</Title>}
            >
              <Divider className="m-0 mb-3" />
              {item.content}
            </Panel>
          </Collapse>
        </Card>
      ))}
    </Space>
  );
};

const PurchaseContent = ({ count, price }: PurchaseContentProps) => (
  <>
    <Row justify="space-between" align="middle">
      <Col>
        <Title level={5}>SL NVL</Title>
      </Col>
      <Col>
        <Text strong>{count}</Text>
      </Col>
    </Row>
    <Row justify="space-between" align="middle">
      <Col>
        <Title level={5}>Thành tiền</Title>
      </Col>
      <Col>
        <Text strong>{price}</Text>
      </Col>
    </Row>
  </>
);

const minimumSeparationAmount = 400000000;
const initSelectedData = {} as ISplitWeightModalData;
const initSeparation = [] as IPurchaseSeparationData[];
const initPurchaseInvoiceDetail = [] as TpurchaseDetail[];

const SeparatePurchaseInvoice = () => {
  const [form] = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { setTitle, setBreadcrumb } = useTitle();

  const { id, priceDetails, purchaseDetail } = location.state || {};
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [selectedData, setSelectedData] =
    useState<ISplitWeightModalData>(initSelectedData);
  const [purchaseSeparation, setPurchaseSeparation] =
    useState<IPurchaseSeparationData[]>(initSeparation);
  const [purchaseInvoiceDetail, setPurchaseInvoiceDetail] = useState<
    TpurchaseDetail[]
  >(initPurchaseInvoiceDetail);

  const hasSeparatedDetails = useMemo(
    () => purchaseSeparation?.length > 0,
    [purchaseSeparation],
  );

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    pageSize = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const dataIndexes: (keyof TpurchaseDetail)[] = useMemo(
    () => [
      'totalGoldWeightThread',
      'q10',
      ...(!hasSeparatedDetails
        ? ([
            'measureWeightThread',
            'lossWeightThread',
            'includeWeightThread',
          ] as (keyof TpurchaseDetail)[])
        : []),
      'totalGoldWeightOfStool',
      'unitPrice',
      'finalPrice',
      'policyPrice',
    ],
    [hasSeparatedDetails],
  );

  const {
    mutate: purchaseInvoiceSeparation,
    isPending: isPurchaseInvoiceSeparationPending,
  } = useMutation({
    mutationFn: (params: IPurchaseInvoiceSeparation) =>
      PurChaseInvoiceApi.purchaseInvoiceSeparation(params),
    onSuccess: (res) => {
      if (res?.data?.purchaseOrderSeparation?.code === '200') {
        const data: IPurchaseSeparationData[] =
          res?.data?.purchaseOrderSeparation?.data;
        setPurchaseSeparation(data);

        toastSuccess(res?.data?.purchaseOrderSeparation?.message);
        return;
      }

      toastError(res?.data?.purchaseOrderSeparation?.message);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: cancelPurchaseInvoiceSeparation,
    isPending: isCancelPurchaseInvoiceSeparationPending,
  } = useMutation({
    mutationFn: (id: string) =>
      PurChaseInvoiceApi.cancelPurchaseInvoiceSeparation(id),
    onSuccess: (res) => {
      if (res?.data?.cancelPurchaseOrderSeparation?.code === '200') {
        setPurchaseSeparation(initSeparation);
        toastSuccess(res?.data?.cancelPurchaseOrderSeparation?.message);
        return;
      }

      toastError(res?.data?.cancelPurchaseOrderSeparation?.message);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    mutate: purchaseInvoiceItemSeparation,
    data: purchaseInvoiceItemSeparationData,
    isPending: isPurchaseInvoiceItemSeparationPending,
  } = useMutation({
    mutationFn: (params: ICalculateItemSeparationPrice) =>
      PurChaseInvoiceApi.purchaseInvoiceItemSeparation(params),
    onSuccess: (res: any) => {
      console.log(res);

      if (res?.data?.purchaseOrderItemSeparation?.code === '200') {
        setIsModalVisible(false);
        getPurchaseOrderSeparation(id);
        toastSuccess(res?.data?.purchaseOrderItemSeparation?.message);
        return;
      }

      toastError(res?.errors?.[0]?.message || 'Có lỗi xảy ra');
    },
    onError: (errors: any) => {
      console.log(errors);
      toastError(errors?.[0]?.message || 'Có lỗi xảy ra');
    },
  });

  const { mutate: cancelPurchaseInvoiceItemSeparation } = useMutation({
    mutationFn: (params: ICancelPurchaseInvoiceItemSeparation) =>
      PurChaseInvoiceApi.cancelPurchaseInvoiceItemSeparation(params),
    onSuccess: (res: any) => {
      console.log(res);

      if (res?.data?.cancelPurchaseOrderItemSeparation?.code === '200') {
        setIsModalVisible(false);
        getPurchaseOrderSeparation(id);
        toastSuccess(res?.data?.cancelPurchaseOrderItemSeparation?.message);
        return;
      }

      toastError(
        res?.data?.cancelPurchaseOrderItemSeparation?.message ||
          'Có lỗi xảy ra',
      );
    },
    onError: (errors: any) => {
      console.log(errors);
      toastError(errors?.[0]?.message || 'Có lỗi xảy ra');
    },
  });

  const {
    mutate: getPurchaseOrderSeparation,
    isPending: isGetPurchaseOrderSeparationPending,
  } = useMutation({
    mutationFn: (id: string) =>
      PurChaseInvoiceApi.getPurchaseOrderSeparation(id),
    onSuccess: (res: any) => {
      if (res?.data?.getPurchaseOrderSeparation?.code === '200') {
        const purchaseSeparation = res?.data?.getPurchaseOrderSeparation?.data;
        setPurchaseSeparation(purchaseSeparation);
        return;
      }

      setPurchaseSeparation(initSeparation);
    },
    onError: (errors: any) => {
      console.log(errors);
      toastError(errors?.[0]?.message || 'Có lỗi xảy ra');
    },
  });

  const { mutate: movePurchaseInvoiceItem } = useMutation({
    mutationFn: (params: IMovePurchaseInvoiceItemParams) =>
      PurChaseInvoiceApi.movePurchaseInvoiceItem(params),
    onSuccess: (res: any) => {
      if (res?.data?.movePurchaseOrderItem?.code === '200') {
        getPurchaseOrderSeparation(id);
        toastSuccess(res?.data?.movePurchaseOrderItem?.message);
        return;
      }

      toastError(res?.errors?.[0]?.message || 'Có lỗi xảy ra');
    },
    onError: (errors: any) => {
      console.log(errors);
      toastError(
        errors?.response?.data?.errors?.[0]?.message || 'Có lỗi xảy ra',
      );
    },
  });

  const {
    mutate: savePurchaseOrderSeparation,
    isPending: isSavePurchaseOrderSeparationPending,
  } = useMutation({
    mutationFn: (id: string) =>
      PurChaseInvoiceApi.savePurchaseOrderSeparation(id),
    onSuccess: (res: any) => {
      toastSuccess(res?.data?.savePurchaseOrderSeparation?.message);
      navigate(path.PURCHASE_INVOICE);
    },
    onError: (errors: any) => {
      console.log(errors);
      toastError(errors?.response?.data?.message || 'Có lỗi xảy ra');
    },
  });

  useEffect(() => {
    if (!id || !purchaseDetail) {
      navigate(path.PURCHASE_INVOICE);
      return;
    }

    setTitle(id);
    setBreadcrumb([
      { title: 'Kế toán/Thủ quỹ' },
      { title: 'Bảng dẻ', href: path.PURCHASE_INVOICE },
      { title: id },
    ]);
    getPurchaseOrderSeparation(id);
  }, [id, purchaseDetail]);

  useEffect(() => {
    const purchaseInvoiceDetail = (purchaseDetail as TpurchaseDetail[])?.map(
      (item) => ({
        ...item,
        totalGoldWeightThread: item?.totalWeightThreadAccounting
          ? item?.totalWeightThreadAccounting
          : item?.totalGoldWeightThread,
        q10: item?.q10Accounting ? item?.q10Accounting : item?.q10,
        measureWeightThread: item?.measureWeightThreadAccounting
          ? item?.measureWeightThreadAccounting
          : item?.measureWeightThread,
        lossWeightThread: item?.lossWeightThreadAccounting
          ? item?.lossWeightThreadAccounting
          : item?.lossWeightThread,
        includeWeightThread: item?.includeWeightThreadAccounting
          ? item?.includeWeightThreadAccounting
          : item?.includeWeightThread,
        totalGoldWeightOfStool: item?.totalWeightStoolAccounting
          ? item?.totalWeightStoolAccounting
          : item?.totalGoldWeightOfStool,
        finalPrice: item?.finalPriceAccounting
          ? item?.finalPriceAccounting
          : item?.finalPrice,
      }),
    );

    setPurchaseInvoiceDetail(purchaseInvoiceDetail);
  }, [purchaseDetail]);

  const formatToNumber = (value: string) => {
    const formattedString = value?.replace(/\./g, '')?.replace(',', '.');
    return parseFloat(formattedString);
  };

  const handleOk = async (values: any) => {
    if (!Object.keys(values).length) {
      toastError('Không tìm thấy dữ liệu khi tách trọng lượng');
      return;
    }

    const params: ICalculateItemSeparationPrice = {
      purchaseOrderId: values?.purchaseOrderId,
      purchaseOrderDetailId: values?.purchaseOrderDetailId,
      items: values?.splitItems?.map((item: any) => ({
        weightThread: formatToNumber(item?.weightThread),
      })),
    };
    purchaseInvoiceItemSeparation(params);
  };

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedValue = Number(value.replace(/\D/g, ''));

    form.setFieldValue('amount', formattedValue.toLocaleString('vi-VN'));
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleCancelPurchaseSeparation = () => {
    cancelPurchaseInvoiceSeparation(id);
    form.resetFields();
  };

  const handleCheckValid = async (totalPrice: number, amount: number) => {
    // if (totalPrice < minimumSeparationAmount) {
    //   toastError(
    //     `Chỉ được tách bảng dẻ có tổng thành tiền trên ${minimumSeparationAmount.toLocaleString('vi-VN')} VND`,
    //   );
    //   return false;
    // }

    if (amount > totalPrice) {
      toastError('Số tiền trung bình không được lớn hơn tổng giá trị đơn');
      return false;
    }

    if (!amount) {
      toastError('Vui lòng nhập số tiền trung bình');
      return false;
    }

    return true;
  };

  const handlePurchaseSeparate = async (values: any) => {
    const { amount } = values;
    const formattedAmount = Number(amount?.replace(/\D/g, ''));

    const totalPrice = purchaseDetail?.reduce(
      (acc: number, { finalPrice, finalPriceAccounting }: TPurchaseDetail) =>
        (acc += finalPriceAccounting ? finalPriceAccounting : finalPrice),
      0,
    );

    const isValid = await handleCheckValid(totalPrice, formattedAmount);
    if (!isValid) return;

    purchaseInvoiceSeparation({
      id,
      amount: Number(amount?.replace(/\D/g, '')),
    });
  };

  const handleMoveSeparationItem = (
    fromIndex: number,
    toIndex: number,
    recordId: string,
  ) => {
    const params: IMovePurchaseInvoiceItemParams = {
      fromId: purchaseSeparation[fromIndex].id,
      toId: purchaseSeparation[toIndex].id,
      detailId: recordId,
    };

    movePurchaseInvoiceItem(params);
  };

  const handleOptionMenuProps = useCallback(
    (tableIndex: number, rowIndex: number, record: IPurchaseSeparationData) => {
      const isParent = !!(
        record?.tpurchaseDetails?.[rowIndex]?.remainingWeightThread ||
        record?.tpurchaseDetails?.[rowIndex]?.remainingFinalPrice
      );

      const isChildren = !!record?.tpurchaseDetails?.[rowIndex]?.parentId;

      return {
        items: [
          {
            key: '1',
            label: 'Chuyển qua',
            disabled: purchaseSeparation?.length <= 1,
            children: purchaseSeparation?.map((_, index) => ({
              key: `1-${index}`,
              label: `Đơn ${index + 1}`,
              disabled: index === tableIndex,
              onClick: () =>
                handleMoveSeparationItem(
                  tableIndex,
                  index,
                  record?.tpurchaseDetails?.[rowIndex]?.id,
                ),
            })),
            onClick: () => console.log('Chuyển qua'),
          },
          {
            key: '2',
            label: 'Tách NVL',
            disabled: isParent || isChildren,
            onClick: () => handleClickItemSeparate(rowIndex, record),
          },
          {
            key: '3',
            label: 'Huỷ tách',
            disabled: !isParent,
            onClick: () => handleCancelItemSeparation(rowIndex, record),
          },
        ],
      };
    },
    [
      hasSeparatedDetails,
      purchaseSeparation,
      purchaseInvoiceItemSeparationData,
    ],
  );

  const handleClickItemSeparate = (
    index: number,
    record: IPurchaseSeparationData,
  ) => {
    const dataIndex = record?.tpurchaseDetails?.[index];
    const splitWeightModalData: ISplitWeightModalData = {
      ...dataIndex,
      purchaseOrderId: record?.id,
      totalGoldWeightThread: dataIndex?.remainingWeightThread
        ? dataIndex?.remainingWeightThread
        : dataIndex?.totalGoldWeightThread,
    };

    if (!Object.keys(splitWeightModalData)?.length) {
      toastError('Không tìm thấy thông tin bảng dẻ');
      return;
    }

    setSelectedData(splitWeightModalData);
    setIsModalVisible(true);
  };

  const handleCancelItemSeparation = (
    index: number,
    record: IPurchaseSeparationData,
  ) => {
    const dataIndex = record?.tpurchaseDetails?.[index];
    const params: ICancelPurchaseInvoiceItemSeparation = {
      id: record?.id,
      detailId: dataIndex?.id,
    };

    cancelPurchaseInvoiceItemSeparation(params);
  };

  const handleSavePurchaseOrderSeparation = () => {
    if (!id) {
      toastError('Không tìm thấy đơn dẻ');
      return;
    }

    savePurchaseOrderSeparation(id);
  };

  const customCollapseItems: CustomCollapseProps[] = useMemo(
    () => [
      {
        panelKey: 'split',
        header: 'Tách đơn',
        content: (
          <>
            <Form form={form} onFinish={handlePurchaseSeparate}>
              <Form.Item
                name="amount"
                className="mb-1"
                labelCol={{ span: 24 }}
                label="Số tiền trung bình"
              >
                <InputForm
                  min={0}
                  size="large"
                  allowClear={false}
                  suffix={<p>VND</p>}
                  className="rounded-xl"
                  onChange={handleAmountChange}
                />
              </Form.Item>
              <Flex gap={8}>
                <Button
                  title="Hủy"
                  type="button"
                  customClass="w-full"
                  disabled={!hasSeparatedDetails}
                  handleOnclick={handleCancelPurchaseSeparation}
                  loading={
                    isCancelPurchaseInvoiceSeparationPending ||
                    isSavePurchaseOrderSeparationPending
                  }
                />
                <Button
                  fill
                  type="submit"
                  title="Gợi ý tách"
                  customClass="w-full"
                  disabled={hasSeparatedDetails}
                  loading={
                    isPurchaseInvoiceSeparationPending ||
                    isSavePurchaseOrderSeparationPending
                  }
                />
              </Flex>
            </Form>
          </>
        ),
      },
      {
        panelKey: 'total',
        header: 'Đơn dẻ tổng',
        content: (
          <PurchaseContent
            count={purchaseDetail?.length}
            price={formatNumber(
              purchaseDetail?.reduce(
                (
                  acc: number,
                  { finalPrice, finalPriceAccounting }: TPurchaseDetail,
                ) =>
                  (acc += finalPriceAccounting
                    ? finalPriceAccounting
                    : finalPrice),
                0,
              ),
            )}
          />
        ),
      },
      ...(hasSeparatedDetails
        ? purchaseSeparation?.map((item, index) => ({
            panelKey: index.toString(),
            header: `Bảng dẻ ${index + 1}`,
            content: (
              <PurchaseContent
                count={item?.tpurchaseDetails?.length}
                price={formatNumber(
                  item?.tpurchaseDetails.reduce(
                    (acc, currentVal) =>
                      (acc += currentVal?.remainingFinalPrice
                        ? currentVal?.remainingFinalPrice
                        : currentVal?.finalPrice),
                    0,
                  ),
                )}
              />
            ),
          }))
        : []),
    ],
    [
      form,
      purchaseDetail,
      purchaseSeparation,
      hasSeparatedDetails,
      isPurchaseInvoiceSeparationPending,
      isSavePurchaseOrderSeparationPending,
      isCancelPurchaseInvoiceSeparationPending,
    ],
  );

  const columns: ColumnsTypeCustom = useMemo(
    () => [
      {
        title: 'STT',
        render: (_, __, index: number) => index + 1 + pageSize * (page - 1),
      },
      { title: 'Mã NVL', dataIndex: ['tproduct', 'id'] },
      {
        title: 'Tên NVL',
        dataIndex: ['tproduct', 'name'],
      },
      {
        align: 'right',
        title: 'TL(chỉ)',
        dataIndex: 'totalGoldWeightThread',
        render: (value) => formatValue(value),
      },
      {
        align: 'right',
        title: 'Vàng Q10 (chỉ)',
        dataIndex: 'q10',
        render: (value) => formatValue(value),
      },
      ...(!hasSeparatedDetails
        ? [
            {
              align: 'right' as const,
              title: 'TL BAO GỒM HỘT(chỉ)',
              dataIndex: 'measureWeightThread',
              render: (value: any) => formatValue(value),
            },
            {
              align: 'right' as const,
              title: 'TL bụi bẩn (chỉ)',
              dataIndex: 'lossWeightThread',
              render: (value: any) => formatValue(value),
            },
            {
              align: 'right' as const,
              title: 'TL HỘT(chỉ)',
              dataIndex: 'includeWeightThread',
              render: (value: any) => formatValue(value),
            },
          ]
        : []),
      {
        align: 'right',
        title: 'TL (phân)',
        dataIndex: 'totalGoldWeightOfStool',
        render: (value) => formatValue(value),
      },
      {
        width: 100,
        align: 'right',
        title: 'Đơn giá (phân)',
        dataIndex: 'unitPrice',
        render: (value) =>
          value === null || value === undefined
            ? '-'
            : new Intl.NumberFormat('vi-VN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(value),
      },
      {
        width: 100,
        align: 'right',
        currency: 'vi-VN',
        title: 'Thành tiền (phân)',
        dataIndex: 'finalPrice',
        render: (_, record: TPurchaseSeparationDetail) =>
          formatNumber(
            record?.remainingFinalPrice
              ? record?.remainingFinalPrice
              : record?.finalPrice,
          ),
      },
      {
        width: 150,
        align: 'right',
        currency: 'vi-VN',
        title: 'Tiền chính sách',
        dataIndex: 'policyPrice',
        render: (value) => formatNumber(value),
      },
      {
        title: 'Ngày tạo',
        dataIndex: 'createDate',
        render: (value) => formatDate(value),
      },
      {
        width: 180,
        title: 'Người cập nhật',
        dataIndex: ['tmodifyUser', 'fullName'],
      },
      {
        width: 200,
        title: 'Ngày cập nhật',
        dataIndex: 'modifyDate',
        render: (value: string) =>
          value ? dayjs(value).format('DD/MM/YYYY HH:MM') : '-',
      },
    ],
    [page, pageSize, hasSeparatedDetails],
  );

  const generateColumns = (
    currentIndex: number,
    item: IPurchaseSeparationData,
  ) => [
    ...columns,
    {
      width: 50,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => {
        const menu = handleOptionMenuProps(currentIndex, index, item);
        return <OptionsMenu className="border-none" menu={menu} />;
      },
    },
  ];

  const tableItems: ITableItem[] = useMemo(
    () => [
      {
        dataSource: hasSeparatedDetails ? null : purchaseInvoiceDetail,
        columns: columns,
      },
      ...(hasSeparatedDetails
        ? purchaseSeparation?.map((purchaseSeparationItem, index) => {
            return {
              title: `Bảng dẻ ${index + 1}`,
              dataSource: purchaseSeparationItem?.tpurchaseDetails?.map(
                (item) => ({
                  ...item,
                  q10: item?.remainingQ10 ? item?.remainingQ10 : item?.q10,
                  totalGoldWeightThread: item?.remainingWeightThread
                    ? item?.remainingWeightThread
                    : item?.totalGoldWeightThread,
                  totalGoldWeightOfStool: item?.remainingWeightStool
                    ? item?.remainingWeightStool
                    : item.totalGoldWeightOfStool,
                  finalPrice: item?.remainingFinalPrice
                    ? item?.remainingFinalPrice
                    : item?.finalPrice,
                }),
              ),
              columns: generateColumns(index, purchaseSeparationItem),
            };
          })
        : []),
    ],
    [columns, purchaseSeparation, hasSeparatedDetails, purchaseInvoiceDetail],
  );

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <Button
            title="Xác nhận"
            displayType="approve"
            disabled={!hasSeparatedDetails}
            loading={
              isPurchaseInvoiceSeparationPending ||
              isCancelPurchaseInvoiceSeparationPending ||
              isSavePurchaseOrderSeparationPending
            }
            handleOnclick={handleSavePurchaseOrderSeparation}
          />
        </Col>
      </Row>

      <div className="grid grid-cols-10 gap-6 mt-4">
        {/* Left Section */}
        {/* <Space direction="vertical" size="middle" className="col-span-2"> */}
        <Flex vertical gap={16} className="col-span-2">
          <CustomCollapse items={customCollapseItems} />
          <Card className="rounded-3xl">
            {(priceDetails as IPriceDetail[])?.map(
              ({ label, value, className }, index) => (
                <Row
                  key={index}
                  align="middle"
                  justify="space-between"
                  className="mb-2"
                >
                  <Col>
                    <h3 className="font-bold">{label}</h3>
                  </Col>
                  <Col>
                    <Text className={`text-start ${className}`}>
                      {value
                        ? typeof value === 'number'
                          ? formatNumber(value)
                          : value
                        : '-'}
                    </Text>
                  </Col>
                </Row>
              ),
            )}
          </Card>
        </Flex>
        {/* </Space> */}

        {/* Right Section */}
        <Space direction="vertical" size="middle" className="col-span-8 w-full">
          {tableItems.map(
            (item, index) =>
              item.dataSource && (
                <Card key={index} className="rounded-3xl">
                  {item.title && <Title level={4}>{item.title}</Title>}
                  <Table
                    pagination={false}
                    columns={item.columns}
                    dataSource={item.dataSource}
                    loading={isGetPurchaseOrderSeparationPending}
                    scroll={{ x: 'max-content' }}
                    summary={(pageData) => {
                      const data = pageData as TPurchaseSeparationDetail[];

                      if (data.length <= 0) return;
                      const total = totalData(data, totalKeys, dataIndexes);
                      const remainingFinalPrice = data?.reduce(
                        (acc, { remainingFinalPrice, finalPrice }) => {
                          acc += remainingFinalPrice
                            ? remainingFinalPrice
                            : finalPrice;
                          return acc;
                        },
                        0,
                      );

                      const finalTotalData = {
                        ...total,
                        finalPrice: remainingFinalPrice,
                      };

                      return (
                        <Summary fixed>
                          <Summary.Row>
                            <Summary.Cell colSpan={3} index={0}>
                              <Text strong>Tổng cộng</Text>
                            </Summary.Cell>
                            {dataIndexes.map((dataIndex, index) => {
                              const isCellPrice = dataIndex.includes('Price');

                              return (
                                <Summary.Cell
                                  key={dataIndex}
                                  index={index + 1}
                                  className="font-bold text-right"
                                >
                                  {/* {formatNumber(finalTotalData[dataIndex])} */}
                                  {/* {formatCurrency({
                                    value: finalTotalData?.[dataIndex],
                                    format: !isCellPrice ? 3 : 0,
                                  })} */}
                                  {formatValue(finalTotalData?.[dataIndex])}
                                </Summary.Cell>
                              );
                            })}
                          </Summary.Row>
                        </Summary>
                      );
                    }}
                  />
                </Card>
              ),
          )}
        </Space>
      </div>

      <SplitWeightModal
        data={selectedData}
        isVisible={isModalVisible}
        loading={isPurchaseInvoiceItemSeparationPending}
        onOk={handleOk}
        onCancel={handleCancel}
      />
    </>
  );
};

export default SeparatePurchaseInvoice;
